/**
 * IDScanConfirmation — confirmation modal shown after a successful
 * scan but BEFORE writing to the patient record.
 *
 * Implements ID Scan Spec §§4, 10.
 *
 * Behavior:
 *   - Shows every parsed field as an editable input
 *   - For each field where existing patient data differs from scanned,
 *     shows both values side-by-side and lets user pick
 *   - Confirm and Fill writes to patient demographics ONLY (never incident,
 *     dispatch, billing, etc.)
 *   - Rescan returns to the scanner
 *   - Cancel discards the scan
 */

import { useState } from 'react';
import type { AAMVAData } from '@/lib/aamva-parser';
import type { EPCR } from '@/types';
import { log } from '@/lib/id-scan-logger';
import { cn } from '@/lib/cn';
import {
  CheckCircle2, RefreshCw, X, AlertTriangle, ScanLine,
  ArrowLeft, ArrowRight,
} from 'lucide-react';

interface Props {
  scan: AAMVAData;
  existingPatient: EPCR;
  onConfirm: (updates: Partial<EPCR>) => void;
  onRescan: () => void;
  onCancel: () => void;
}

interface FieldRow {
  key: keyof EPCR;
  label: string;
  scanned: string | null;
  existing: string | null;
  type?: 'text' | 'date' | 'select';
  options?: string[];
}

export function IDScanConfirmation({ scan, existingPatient, onConfirm, onRescan, onCancel }: Props) {
  // Build the diff/merge view from scanned + existing data
  const rows: FieldRow[] = [
    { key: 'patient_first_name', label: 'First name', scanned: scan.firstName, existing: existingPatient.patient_first_name },
    { key: 'patient_middle_name', label: 'Middle name', scanned: scan.middleName, existing: existingPatient.patient_middle_name },
    { key: 'patient_last_name', label: 'Last name', scanned: scan.lastName, existing: existingPatient.patient_last_name },
    { key: 'patient_suffix', label: 'Suffix', scanned: scan.suffix, existing: existingPatient.patient_suffix },
    { key: 'patient_dob', label: 'Date of birth', scanned: scan.dateOfBirth, existing: existingPatient.patient_dob, type: 'date' },
    { key: 'patient_gender', label: 'Sex (per ID)', scanned: scan.gender, existing: existingPatient.patient_gender, type: 'select', options: ['Male', 'Female', 'Unknown', 'Non-Binary'] },
    { key: 'patient_address', label: 'Street address', scanned: scan.street, existing: existingPatient.patient_address },
    { key: 'patient_city', label: 'City', scanned: scan.city, existing: existingPatient.patient_city },
    { key: 'patient_state', label: 'State', scanned: scan.state, existing: existingPatient.patient_state },
    { key: 'patient_zip', label: 'ZIP', scanned: scan.zip, existing: existingPatient.patient_zip },
    { key: 'patient_country', label: 'Country', scanned: scan.country, existing: existingPatient.patient_country },
    { key: 'patient_id_number', label: 'ID number', scanned: scan.licenseNumber, existing: existingPatient.patient_id_number },
    { key: 'patient_id_state', label: 'ID jurisdiction', scanned: scan.issuingJurisdiction, existing: existingPatient.patient_id_state },
    { key: 'patient_id_expiration', label: 'ID expiration', scanned: scan.expirationDate, existing: existingPatient.patient_id_expiration, type: 'date' },
    { key: 'patient_id_issue_date', label: 'ID issue date', scanned: scan.issueDate, existing: existingPatient.patient_id_issue_date, type: 'date' },
    { key: 'patient_eye_color', label: 'Eye color', scanned: scan.eyeColor, existing: existingPatient.patient_eye_color },
  ];

  // Initial merged values: prefer scanned when field is empty, otherwise keep existing
  // until user explicitly chooses (visible diff)
  const [values, setValues] = useState<Record<string, string | null>>(() => {
    const out: Record<string, string | null> = {};
    for (const r of rows) {
      if (r.scanned && !r.existing) {
        out[r.key] = r.scanned;
      } else if (r.existing && !r.scanned) {
        out[r.key] = r.existing;
      } else if (r.scanned && r.existing && r.scanned !== r.existing) {
        // Conflict — leave at existing, user must choose
        out[r.key] = r.existing;
      } else {
        out[r.key] = r.scanned ?? r.existing ?? null;
      }
    }
    return out;
  });

  const conflicts = rows.filter(
    (r) => r.scanned && r.existing && r.scanned !== r.existing,
  );

  function setField(key: string, value: string | null): void {
    setValues((v) => ({ ...v, [key]: value }));
  }

  function chooseScanned(key: string, value: string | null): void {
    setField(key, value);
    log('merge_conflict', { field: key, choice: 'scanned' });
  }

  function chooseExisting(key: string, value: string | null): void {
    setField(key, value);
    log('merge_conflict', { field: key, choice: 'existing' });
  }

  function handleConfirm(): void {
    const updates: Partial<EPCR> = {};

    for (const r of rows) {
      const v = values[r.key];
      if (v !== r.existing) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (updates as any)[r.key] = v;
      }
    }

    // Always set scan provenance metadata
    const now = new Date().toISOString();
    updates.patient_id_scan_source = 'barcode_scan';
    updates.patient_id_scan_timestamp = now;
    updates.patient_id_scan_status = scan.parseStatus;
    updates.patient_id_raw_payload = scan.raw;
    updates.patient_id_parser_version = scan.parserVersion;
    updates.patient_id_reviewed_by_user = true;
    if (scan.documentType) updates.patient_id_type = scan.documentType;

    log('patient_fields_filled', {
      fields_updated: Object.keys(updates).length,
      conflicts_resolved: conflicts.length,
    });

    onConfirm(updates);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-4">
      <div className="w-full max-w-3xl rounded-xl bg-slate-900 border border-slate-700 overflow-hidden flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3 shrink-0">
          <div className="flex items-center gap-2 text-slate-200">
            <CheckCircle2 size={20} className="text-ems-green" />
            <span className="font-semibold">Review scanned patient information</span>
            <span className="ml-2 rounded bg-brand-500/20 px-2 py-0.5 text-xs text-brand-300">
              Scanned from government ID
            </span>
          </div>
          <button
            onClick={onCancel}
            className="rounded p-2 text-slate-400 hover:bg-slate-800"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Conflicts warning */}
        {conflicts.length > 0 && (
          <div className="border-b border-amber-700 bg-amber-950/30 px-4 py-2 text-xs text-amber-300 flex items-center gap-2 shrink-0">
            <AlertTriangle size={14} />
            <span>
              <strong>{conflicts.length} field{conflicts.length !== 1 ? 's' : ''}</strong> differ
              from existing patient data. Pick which value to keep for each.
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {rows.map((r) => (
              <ConfirmRow
                key={r.key as string}
                row={r}
                value={values[r.key as string]}
                onChange={(v) => setField(r.key as string, v)}
                onChooseScanned={() => chooseScanned(r.key as string, r.scanned)}
                onChooseExisting={() => chooseExisting(r.key as string, r.existing)}
              />
            ))}
          </div>

          {/* Provenance footer */}
          <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-500">
            <div className="font-semibold text-slate-400 mb-1">Scan provenance</div>
            <div>Status: <span className="text-slate-300">{scan.parseStatus}</span></div>
            <div>Fields parsed: <span className="text-slate-300">{scan.fieldsParsed}</span></div>
            <div>Parser: <span className="text-slate-300">{scan.parserVersion}</span></div>
            {scan.parseErrors.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-amber-400">Parse warnings ({scan.parseErrors.length})</summary>
                <ul className="mt-1 ml-4 list-disc">
                  {scan.parseErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700 px-4 py-3 flex flex-wrap gap-2 justify-between shrink-0">
          <button onClick={onRescan} className="btn-secondary text-sm gap-1">
            <RefreshCw size={14} /> Rescan
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} className="btn-secondary text-sm">
              Cancel
            </button>
            <button onClick={handleConfirm} className="btn-primary text-sm gap-1 bg-ems-green hover:bg-green-700">
              <CheckCircle2 size={14} /> Confirm and Fill Patient Info
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Single field row with diff/merge UI
// ============================================================

function ConfirmRow({
  row, value, onChange, onChooseScanned, onChooseExisting,
}: {
  row: FieldRow;
  value: string | null;
  onChange: (v: string | null) => void;
  onChooseScanned: () => void;
  onChooseExisting: () => void;
}) {
  const isConflict = row.scanned && row.existing && row.scanned !== row.existing;
  const onlyScanned = row.scanned && !row.existing;
  const onlyExisting = !row.scanned && row.existing;

  const renderInput = () => {
    if (row.type === 'select' && row.options) {
      return (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="input-field"
        >
          <option value="">—</option>
          {row.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        type={row.type === 'date' ? 'date' : 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="input-field"
      />
    );
  };

  return (
    <div className={cn(
      'rounded-lg border p-3',
      isConflict ? 'border-amber-700 bg-amber-950/20' : 'border-slate-800 bg-slate-900',
    )}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">{row.label}</label>
        {onlyScanned && (
          <span className="rounded bg-brand-500/20 px-1.5 py-0.5 text-[10px] text-brand-300">FROM SCAN</span>
        )}
        {onlyExisting && (
          <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">EXISTING</span>
        )}
        {isConflict && (
          <span className="rounded bg-amber-900 px-1.5 py-0.5 text-[10px] text-amber-300">CONFLICT</span>
        )}
      </div>

      {isConflict ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onChooseScanned}
              className={cn(
                'rounded-lg border p-2 text-left text-xs transition-colors',
                value === row.scanned
                  ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700',
              )}
            >
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-70">
                <ScanLine size={10} /> From scan
              </div>
              <div className="mt-0.5 font-mono text-sm">{row.scanned ?? '—'}</div>
            </button>
            <button
              type="button"
              onClick={onChooseExisting}
              className={cn(
                'rounded-lg border p-2 text-left text-xs transition-colors',
                value === row.existing
                  ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700',
              )}
            >
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-70">
                <ArrowLeft size={10} /> Existing
              </div>
              <div className="mt-0.5 font-mono text-sm">{row.existing ?? '—'}</div>
            </button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 flex items-center gap-1">
              <ArrowRight size={10} /> Or edit:
            </div>
            {renderInput()}
          </div>
        </div>
      ) : (
        renderInput()
      )}
    </div>
  );
}
